import filterAPI from '@/api/master-data/course-mapping-mitra/filter-api';

const state = () => ({
  positions: [],
  trainings: [],
  trainingsPagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPage: 0
  },
  trainingMapping: [],
  selectedTrainings: [], // Array of selected training IDs
  loading: {
    positions: false,
    trainings: false,
    trainingMapping: false,
    saving: false
  }
});

const mutations = {
  SET_POSITIONS(state, positions) {
    state.positions = positions;
  },
  SET_TRAININGS(state, trainings) {
    state.trainings = trainings;
  },
  SET_TRAININGS_PAGINATION(state, pagination) {
    state.trainingsPagination = {
      total: pagination.total || 0,
      page: pagination.page || 1,
      limit: pagination.limit || 10,
      totalPage: pagination.totalPage || 0
    };
  },
  SET_TRAINING_MAPPING(state, trainingMapping) {
    state.trainingMapping = trainingMapping;
  },
  SET_SELECTED_TRAININGS(state, selectedTrainings) {
    // Normalize all IDs to numbers and remove duplicates
    const normalizedIds = selectedTrainings.map(id => Number(id));
    state.selectedTrainings = [...new Set(normalizedIds)];
  },
  TOGGLE_TRAINING_SELECTION(state, trainingId) {
    // Normalize to number for consistent comparison
    const normalizedId = Number(trainingId);
    const index = state.selectedTrainings.findIndex(id => Number(id) === normalizedId);
    
    if (index > -1) {
      state.selectedTrainings.splice(index, 1);
    } else {
      state.selectedTrainings.push(normalizedId);
    }
  },
  SET_LOADING(state, { type, loading }) {
    state.loading[type] = loading;
  },
  CLEAR_SELECTED_TRAININGS(state) {
    state.selectedTrainings = [];
  }
};

const getters = {
  getPositions: (state) => state.positions,
  getTrainings: (state) => state.trainings,
  getTrainingsPagination: (state) => state.trainingsPagination,
  getTrainingMapping: (state) => state.trainingMapping,
  getSelectedTrainings: (state) => state.selectedTrainings,
  getLoading: (state) => (type) => state.loading[type] || false,
  isTrainingSelected: (state) => (trainingId) => {
    // Handle both string and number comparison
    return state.selectedTrainings.some(id => id == trainingId || String(id) === String(trainingId));
  }
};

const actions = {
  async fetchPositions({ commit }) {
    commit('SET_LOADING', { type: 'positions', loading: true });
    
    try {
      const response = await filterAPI.getPositions();
      commit('SET_POSITIONS', response.data.data.data || []);
    } catch (error) {
      console.error('Error fetching positions mitra:', error);
      commit('SET_POSITIONS', []);
    } finally {
      commit('SET_LOADING', { type: 'positions', loading: false });
    }
  },

  async fetchTrainings({ commit }, params = {}) {
    commit('SET_LOADING', { type: 'trainings', loading: true });
    try {
      const response = await filterAPI.getTrainings(params);
      // Extract data from response.data.data
      const responseData = response.data.data || {};
      const trainingsData = responseData.data || [];
      commit('SET_TRAININGS', trainingsData);
      
      // Set pagination data
      commit('SET_TRAININGS_PAGINATION', {
        total: responseData.total || 0,
        page: responseData.page || 1,
        limit: responseData.limit || 10,
        totalPage: responseData.totalPage || 0
      });
    } catch (error) {
      console.error('Error fetching trainings mitra:', error);
      commit('SET_TRAININGS', []);
      commit('SET_TRAININGS_PAGINATION', { total: 0, page: 1, limit: 10, totalPage: 0 });
    } finally {
      commit('SET_LOADING', { type: 'trainings', loading: false });
    }
  },

  async fetchTrainingMapping({ commit }, positionLevelId) {
    commit('SET_LOADING', { type: 'trainingMapping', loading: true });
    try {
      const response = await filterAPI.getTrainingMitra(positionLevelId);
      const mappingData = response.data.data || [];
      commit('SET_TRAINING_MAPPING', mappingData);
      
      // Set selected trainings based on mapping
      // Extract training_id from mapped trainings
      const selectedTrainings = mappingData.map(item => item.training_id);
      commit('SET_SELECTED_TRAININGS', selectedTrainings);
    } catch (error) {
      console.error('Error fetching training mapping mitra:', error);
      commit('SET_TRAINING_MAPPING', []);
      commit('SET_SELECTED_TRAININGS', []);
    } finally {
      commit('SET_LOADING', { type: 'trainingMapping', loading: false });
    }
  },

  async saveContractorTrainingMapping({ commit }, data) {
    commit('SET_LOADING', { type: 'saving', loading: true });
    try {
      const response = await filterAPI.saveContractorTrainingMapping(data);
      return response;
    } catch (error) {
      console.error('Error saving contractor training mapping:', error);
      throw error;
    } finally {
      commit('SET_LOADING', { type: 'saving', loading: false });
    }
  },

  async uploadBulkMapping({ commit }, formData) {
    commit('SET_LOADING', { type: 'saving', loading: true });
    try {
      const response = await filterAPI.uploadBulkMapping(formData);
      return response.data;
    } catch (error) {
      console.error('Error uploading bulk mapping file:', error);
      throw error;
    } finally {
      commit('SET_LOADING', { type: 'saving', loading: false });
    }
  },

  async downloadTemplate({ commit }) {
    commit('SET_LOADING', { type: 'saving', loading: true });
    try {
      return await filterAPI.downloadTemplate();
    } catch (error) {
      console.error('Error downloading contractor training mapping template:', error);
      throw error;
    } finally {
      commit('SET_LOADING', { type: 'saving', loading: false });
    }
  },

  toggleTrainingSelection({ commit }, trainingId) {
    commit('TOGGLE_TRAINING_SELECTION', trainingId);
  },

  clearSelectedTrainings({ commit }) {
    commit('CLEAR_SELECTED_TRAININGS');
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};
